import {
  ADMIN_DASHBOARD,
  AdminDatePickerInputModal,
  AdminSelect,
  ComparedPeriodType,
  Div,
  H3,
  handleSetUrlSearchParams,
  lightTheme,
  MaxRange,
  P,
  PeriodType,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { SetURLSearchParams } from 'react-router-dom';

const comparedOptions = [
  { name: 'Compare to None', value: '' },
  { name: 'Compare to Last Month', value: ComparedPeriodType.LAST_MONTH },
  { name: 'Compare to Last Year', value: ComparedPeriodType.LAST_YEAR },
  { name: 'Compare to Custom', value: ComparedPeriodType.CUSTOM },
];

const filterComparedOptions = (
  <>
    {comparedOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </>
);

const comparedOptions2 = [
  { name: 'None', value: '' },
  { name: 'Last Month', value: ComparedPeriodType.LAST_MONTH },
  { name: 'Last Year', value: ComparedPeriodType.LAST_YEAR },
  { name: 'Custom', value: ComparedPeriodType.CUSTOM },
];

const filterComparedOptions2 = (
  <>
    {comparedOptions2.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </>
);

export const ChartsFilter = ({
  from,
  to,
  compareToTo,
  compareToFrom,
  setSearchParams,
  comparedTo,
  title = 'Overview',
  hideCompare = false,
  extraFilter,
}: {
  from: string;
  to: string;
  compareToTo?: string;
  compareToFrom?: string;
  setSearchParams: SetURLSearchParams;
  comparedTo?: string;
  /** Heading beside the filters; dashboards with tabs name their own. */
  title?: string;
  /** For a backend that has no "compared to" yet: the range picker alone. */
  hideCompare?: boolean;
  /** Another filter in the same row, before the range (a driver picker). */
  extraFilter?: React.ReactNode;
}) => {
  const [modal, setModal] = useState<ComparedPeriodType | false>(false);

  const setDate = (date: Date[]) => {
    const fromFormatted = DateTime.fromJSDate(date[0]).toISO();
    const toFormatted = DateTime.fromJSDate(date[1]).toISO();

    handleSetUrlSearchParams('from', fromFormatted, setSearchParams);
    handleSetUrlSearchParams('to', toFormatted, setSearchParams);
  };

  const date = [
    DateTime.fromISO(from).toJSDate(),
    DateTime.fromISO(to).toJSDate(),
  ];

  const compareToDate = [
    DateTime.fromISO(compareToFrom || '').toJSDate(),
    DateTime.fromISO(compareToTo || '').toJSDate(),
  ];

  const setCompareToDate = (date: Date[]) => {
    const fromFormatted = DateTime.fromJSDate(date[0]).toISO();
    const toFormatted = DateTime.fromJSDate(date[1]).toISO();

    handleSetUrlSearchParams('compareToFrom', fromFormatted, setSearchParams);
    handleSetUrlSearchParams('compareToTo', toFormatted, setSearchParams);
  };

  const setComparedTo = (value: any) => {
    handleSetUrlSearchParams('periodTypeComparedTo', value, setSearchParams);
  };

  const handleChangeComparedTo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    const diffInDays = DateTime.fromISO(to).diff(
      DateTime.fromISO(from),
      'days'
    ).days;

    const dateFromDay = DateTime.fromISO(from).day;
    const dateFromMonth = DateTime.fromISO(from).month;
    const dateToMonth = DateTime.fromISO(to).month;

    if (
      value === ComparedPeriodType.LAST_MONTH &&
      dateFromDay === 1 &&
      dateFromMonth === dateToMonth &&
      diffInDays > 31
    ) {
      return setModal(ComparedPeriodType.LAST_MONTH);
    } else if (
      value === ComparedPeriodType.LAST_MONTH &&
      dateFromMonth !== dateToMonth &&
      diffInDays >= 30
    ) {
      return setModal(ComparedPeriodType.LAST_MONTH);
    } else if (
      value === ComparedPeriodType.LAST_MONTH &&
      dateFromDay !== 1 &&
      diffInDays >= 30
    ) {
      return setModal(ComparedPeriodType.LAST_MONTH);
    } else if (value === ComparedPeriodType.LAST_YEAR && diffInDays > 365) {
      return setModal(ComparedPeriodType.LAST_YEAR);
    }
    setComparedTo(value);
  };

  const maxRange = () => {
    if (
      comparedTo === ComparedPeriodType.LAST_MONTH ||
      modal === ComparedPeriodType.LAST_MONTH
    ) {
      return MaxRange.month;
    } else if (
      comparedTo === ComparedPeriodType.LAST_YEAR ||
      modal === ComparedPeriodType.LAST_YEAR
    ) {
      return MaxRange.year;
    } else {
      return undefined;
    }
  };

  const handleSubmitDateWithValidation = () => {
    if (modal === ComparedPeriodType.LAST_MONTH) {
      setComparedTo(ComparedPeriodType.LAST_MONTH);
    } else if (modal === ComparedPeriodType.LAST_YEAR) {
      setComparedTo(ComparedPeriodType.LAST_YEAR);
    }
  };

  const errorMessage = `When comparing to ${
    maxRange() === MaxRange.month ? 'last month' : 'last year'
  }, the date range can be maximum one ${maxRange()} apart.`;

  const beforeApplyValidation = (inputValue: Date[]) => {
    const diffInDays = DateTime.fromJSDate(inputValue[1]).diff(
      DateTime.fromJSDate(inputValue[0]),
      'days'
    ).days;

    const dateFromDay = DateTime.fromJSDate(inputValue[0]).day;

    if (
      modal === ComparedPeriodType.LAST_MONTH &&
      dateFromDay === 1 &&
      diffInDays > 31
    ) {
      return errorMessage;
    } else if (
      modal === ComparedPeriodType.LAST_MONTH &&
      dateFromDay !== 1 &&
      diffInDays >= 30
    ) {
      return errorMessage;
    } else if (modal === ComparedPeriodType.LAST_YEAR && diffInDays > 365) {
      return errorMessage;
    } else {
      return null;
    }
  };

  return (
    <Div
      $flex
      $alignCenter
      $between
      $m="0px 0 12px"
      $gap="16px"
      $gapSM="8px"
      $columnSM
      // $alignStart
      $alignStartSM
      $width="100%"
      $wrap
      // $columnTablet
      // $alignStartTablet
    >
      <H3 $mb="0" $m="0">
        {title}
      </H3>
      <Div
        $flex
        $alignCenter
        $justifyEnd
        $gap="16px"
        $gapTablet="8px"
        $gapSM="8px"
        $columnSM
        $widthSM="100%"
      >
        {extraFilter}
        <Div $width="240px" $widthSM="100%">
          <AdminDatePickerInputModal
            value={date}
            onChange={setDate}
            enableTime={false}
            mode="range"
            maxRange={maxRange()}
            $shadowDesign
            externalModalState={!!modal}
            setExternalModalState={setModal}
            onSubmit={handleSubmitDateWithValidation}
            beforeApplyValidation={beforeApplyValidation}
            component={
              (modal || comparedTo) && (
                <P $m="16px 0 0 0" $center $color={lightTheme.lightGray}>
                  {errorMessage}
                </P>
              )
            }
          />
        </Div>

        {!hideCompare && location.pathname.includes(ADMIN_DASHBOARD) && (
          <>
            <Div $desktop>
              <P $m="0" $center>
                compared to
              </P>
            </Div>

            <Div $width="170px" $widthTablet="150px" $widthSM="100%">
              <Div $desktop>
                <AdminSelect
                  value={comparedTo}
                  options={filterComparedOptions2}
                  onChange={handleChangeComparedTo}
                  $shadowDesign
                />
              </Div>

              <Div $mobile>
                <AdminSelect
                  value={comparedTo}
                  options={filterComparedOptions}
                  onChange={handleChangeComparedTo}
                  $shadowDesign
                />
              </Div>
            </Div>

            {comparedTo === PeriodType.CUSTOM && (
              <Div $width="240px" $widthSM="100%">
                <AdminDatePickerInputModal
                  $shadowDesign
                  value={compareToDate || []}
                  onChange={(date: Date[]) => {
                    setCompareToDate(date);
                  }}
                  enableTime={false}
                  mode="range"
                />
              </Div>
            )}
          </>
        )}
      </Div>
    </Div>
  );
};
